import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { api, unwrap, type Schemas } from "../api/client";

type Together = Schemas["GpsTogether"];

const time = (iso: string) => iso.slice(11, 19);
const minutes = (s: number) =>
  s < 3600
    ? `${Math.round(s / 60)} min`
    : `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
const span = (a: string, b: string) => (Date.parse(b) - Date.parse(a)) / 1000;

/**
 * The GPS tracks in view compared (alpha feedback): which sources were
 * together, which travelled together, and the places stopped at, with how
 * long and who came back. Clicking a row plays from there.
 */
export function GpsAnalysis({
  range,
  filter,
  onPlay,
}: {
  range: { from: number; to: number };
  filter: { library?: string; case?: string };
  onPlay: (utcMs: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const query = {
    from: new Date(range.from).toISOString(),
    to: new Date(range.to).toISOString(),
    ...filter,
  };
  const gps = useQuery({
    queryKey: [
      "timeline",
      "gps",
      range.from,
      range.to,
      filter.library,
      filter.case,
    ],
    queryFn: () => unwrap(api.GET("/api/timeline/gps", { params: { query } })),
  });
  const d = gps.data;
  if (!d || (d.together.length === 0 && d.places.length === 0)) return null;
  const revisited = d.places.filter((p) => p.revisited).length;
  const pairs = (rows: Together[], label: string) =>
    rows.length > 0 && (
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            <TableCell>{label}</TableCell>
            <TableCell>From (UTC)</TableCell>
            <TableCell>For</TableCell>
            <TableCell>Closest</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((t) => (
            <TableRow
              key={`${t.a}|${t.b}|${t.from_utc}`}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => onPlay(Date.parse(t.from_utc))}
            >
              <TableCell>
                {t.a} and {t.b}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>
                {time(t.from_utc)}
              </TableCell>
              <TableCell>{minutes(span(t.from_utc, t.to_utc))}</TableCell>
              <TableCell>{Math.round(t.closest_m)} m</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="GPS analysis"
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
          GPS analysis
        </Typography>
        <Chip size="small" label={`${d.together.length} together`} />
        <Chip
          size="small"
          label={`${d.travelling.length} travelling together`}
        />
        <Chip
          size="small"
          label={`${d.places.length} places, ${revisited} revisited`}
        />
        <Button size="small" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}
        </Button>
      </Stack>
      {d.truncated && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Too many recordings in view to compare them all: zoom in.
        </Alert>
      )}
      {open && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Together: two GPS sources within 50 m of each other for a minute or
            more. Travelling together: both moving, for two minutes or more.
            Places: where sources stopped for two minutes or more, within 50 m
            counting as one place; longest total time first. Click a row to play
            from there.
          </Typography>
          {pairs(d.together, "Together")}
          {pairs(d.travelling, "Travelling together")}
          {d.places.length > 0 && (
            <Table size="small" aria-label="Places">
              <TableHead>
                <TableRow>
                  <TableCell>Place</TableCell>
                  <TableCell>Total time</TableCell>
                  <TableCell>Visits (UTC)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.places.map((p) => (
                  <TableRow
                    key={`${p.lat}|${p.lon}`}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      p.visits[0] && onPlay(Date.parse(p.visits[0].from_utc))
                    }
                  >
                    <TableCell
                      sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
                    >
                      {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                      {p.revisited && (
                        <Chip size="small" label="Revisited" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{minutes(p.dwell_s)}</TableCell>
                    <TableCell>
                      {p.visits
                        .map(
                          (v) =>
                            `${v.source} ${time(v.from_utc)}–${time(v.to_utc)}`,
                        )
                        .join("; ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      )}
    </Paper>
  );
}
